import { captureException } from '@sentry/nextjs';
import type { ImageLoaderProps } from 'next/image';

const CDN_HOST = 'cdn.welfarehello.com';

/**
 * CloudFront Lambda@Edge 이미지 최적화 로더
 *
 * - 상대 경로: 원본 반환 → Next.js가 /_next/image로 자동 처리
 * - CDN URL: Lambda@Edge 최적화 파라미터 추가
 * - 외부 URL: 원본 그대로 반환
 *
 * @param src - 이미지 경로 (절대/상대/URL)
 * @param width - 너비
 * @param quality - 품질 (1-100, 기본: 75)
 * @returns 최적화 이미지 URL
 *
 * @example
 * // 정적 assets → 원본 반환 → Next.js가 /_next/image로 처리
 * imageLoader({ src: '/assets/logo.png', width: 800 })
 * // => '/assets/logo.png' (Next.js가 /_next/image?url=...로 변환)
 *
 * // welfarehello CDN URL → 최적화
 * imageLoader({ src: 'https://cdn.welfarehello.com/image.jpg', width: 800 })
 * // => https://cdn.welfarehello.com/image.jpg?w=800&q=75&f=webp
 *
 * // 외부 URL → 원본 반환
 * imageLoader({ src: 'https://example.com/image.jpg', width: 800 })
 * // => https://example.com/image.jpg
 */
export default function imageLoader({
  src,
  width,
  quality = 75,
}: ImageLoaderProps): string {
  try {
    // 완전한 URL인 경우
    if (src.startsWith('http://') || src.startsWith('https://')) {
      const url = new URL(src);

      // welfarehello CDN이 아니면 원본 반환
      if (!url.hostname.includes(CDN_HOST)) {
        return src;
      }

      // GIF는 변환 시 애니메이션이 깨지므로 원본 반환
      if (url.pathname.toLowerCase().endsWith('.gif')) {
        return url.toString();
      }

      // CDN 최적화 파라미터 추가
      url.searchParams.set('w', width.toString());
      url.searchParams.set('q', quality.toString());
      //* f=auto는 AVIF를 고른다 — 엣지 miss 인코딩이 webp의 2배(실측 0.56s vs 0.32s)인데 절감은 3.6KB뿐
      url.searchParams.set('f', 'webp');

      return url.toString();
    }

    // 상대 경로 → 정적 자원
    // GIF는 애니메이션 보존을 위해 원본 반환
    if (src.toLowerCase().split('?')[0].endsWith('.gif')) {
      return src;
    }

    const separator = src.includes('?') ? '&' : '?';

    return `${src}${separator}w=${width}&q=${quality}`;
  } catch (error) {
    // URL 파싱 실패 시 원본 반환
    captureException(error);

    return src;
  }
}
